/**
 * GovernmentSupport — Landing, Códice Botánico edition.
 * Mexican resources & food-sovereignty section. SVG icons, codex palette.
 */
import React from 'react';
import { motion } from 'framer-motion';

const initiatives = [
  {
    title: 'Tiendas Bienestar',
    description: '25,600 puntos de venta',
    details: [
      'Catálogo de productos marca Bienestar',
      'Encuentra tiendas cercanas',
      'Productos nacionales accesibles',
      'Maíz, frijol, arroz, cacao, café, miel',
    ],
    color: 'var(--cdx-jade)',
    Icon: StoreIcon,
  },
  {
    title: 'Productos mexicanos',
    description: 'De productores locales',
    details: [
      'Productos 100% mexicanos identificados',
      'Información de origen por estado',
      'Directo del productor',
      'Calidad y frescura garantizada',
    ],
    color: 'var(--cdx-jamaica)',
    Icon: FlagIcon,
  },
  {
    title: 'Etiquetado NOM-051',
    description: 'Información nutricional clara',
    details: [
      'Sellos de advertencia en productos',
      'Criterios actualizados 2025',
      'Compara productos fácilmente',
      'Decisiones informadas',
    ],
    color: 'var(--cdx-cempasuchil)',
    Icon: TagIcon,
  },
  {
    title: 'Alimentación escolar',
    description: 'Opciones saludables en escuelas',
    details: [
      'Guías para cafeterías escolares',
      'Menús balanceados y nutritivos',
      'Productos regionales disponibles',
      'Educación nutricional infantil',
    ],
    color: 'var(--cdx-turquesa)',
    Icon: SchoolIcon,
  },
];

const stats = [
  ['25,600', 'Tiendas Bienestar a nivel nacional'],
  ['56+', 'Alimentos SMAE catalogados'],
  ['100%', 'NOM-051 Fase 3 compliant'],
  ['1M+', 'Meta de usuarios impactados'],
];

const GovernmentSupport = () => (
  <section
    className="cdx-grain relative"
    style={{ padding: '88px 0', background: 'var(--cdx-paper)', fontFamily: 'var(--cdx-font-body)' }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1]">
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="text-center mb-14"
      >
        <span className="cdx-kicker">Soberanía alimentaria</span>
        <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '10px 0 12px', color: 'var(--cdx-ink)' }}>
          Recursos disponibles en México
        </h2>
        <p style={{ fontSize: 18, color: 'var(--cdx-ink-soft)', maxWidth: 640, margin: '0 auto' }}>
          Conoce las herramientas y programas que te ayudan a{' '}
          <span style={{ fontWeight: 600, color: 'var(--cdx-jade)' }}>tomar mejores decisiones alimentarias</span>.
        </p>
      </motion.div>

      {/* Initiatives */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {initiatives.map((it, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: index * 0.08, duration: 0.5 }}
            style={{
              background: 'var(--cdx-paper-2)', border: '1px solid var(--cdx-paper-edge)',
              borderRadius: 'var(--cdx-r-lg)', overflow: 'hidden', boxShadow: 'var(--cdx-shadow-sm)',
            }}
          >
            <div style={{ background: it.color, padding: '20px 20px', color: 'var(--cdx-nighttext)' }}>
              <div style={{ marginBottom: 10, opacity: 0.95 }}><it.Icon /></div>
              <h3 className="cdx-display" style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>{it.title}</h3>
              <p style={{ fontSize: 12.5, opacity: 0.85 }}>{it.description}</p>
            </div>
            <ul style={{ margin: 0, padding: '16px 18px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {it.details.map((d, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: 'var(--cdx-ink-soft)' }}>
                  <Check color={it.color} /> <span>{d}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Impact stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="cdx-grain"
        style={{
          marginTop: 56, position: 'relative', overflow: 'hidden',
          background: 'var(--cdx-paper-2)', borderRadius: 'var(--cdx-r-xl)',
          border: '1px solid var(--cdx-paper-edge)', boxShadow: 'var(--cdx-shadow-md)',
        }}
      >
        <div className="cdx-greca cdx-greca" />
        <div style={{ padding: '28px', position: 'relative', zIndex: 1 }}>
          <h3 className="cdx-display" style={{ fontSize: 22, textAlign: 'center', color: 'var(--cdx-ink)', marginBottom: 26 }}>
            Impacto en México
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map(([num, label]) => (
              <div key={label} className="text-center">
                <div className="cdx-display" style={{ fontSize: 34, fontWeight: 600, color: 'var(--cdx-jade)', marginBottom: 6 }}>{num}</div>
                <div style={{ fontSize: 13, color: 'var(--cdx-ink-soft)', lineHeight: 1.4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Food culture */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6 }}
        style={{ marginTop: 56 }}
      >
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <h3 className="cdx-display" style={{ fontSize: 26, textAlign: 'center', color: 'var(--cdx-ink)', marginBottom: 10 }}>
            México: 7.º exportador agroalimentario mundial
          </h3>
          <p style={{ fontSize: 17, color: 'var(--cdx-ink-soft)', textAlign: 'center', marginBottom: 32, lineHeight: 1.6 }}>
            Nuestros productos de calidad superior alimentan al mundo. Es momento de que también alimenten a México.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <InfoCard title="Lo mejor de México al mundo" color="var(--cdx-jade)" Icon={GlobeIcon} bullet="check"
              items={[
                'Aguacate, berries y tomate premium',
                'Café, cacao y miel de reconocimiento internacional',
                'Maíz nativo, frijol y cultivos ancestrales',
                '750,000 productores trabajando con excelencia',
              ]} />
            <InfoCard title="Un desafío nacional" color="var(--cdx-copal)" Icon={AlertIcon} bullet="dot"
              items={[
                '4.º lugar mundial en consumo de ultraprocesados',
                'Crisis de obesidad y diabetes afectando a millones',
                'Economía familiar impactada por la mala alimentación',
                'Los productores locales necesitan nuestro apoyo',
              ]} />
          </div>

          <div className="cdx-grain" style={{
            position: 'relative', overflow: 'hidden', background: 'var(--cdx-jade-deep)',
            borderRadius: 'var(--cdx-r-xl)', padding: '28px 26px', textAlign: 'center',
          }}>
            <div className="cdx-greca--gold cdx-greca" style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
            <p className="cdx-display" style={{ fontSize: 21, color: 'var(--cdx-nighttext)', marginBottom: 10, position: 'relative', zIndex: 1 }}>
              Tu elección alimentaria es un voto por la salud de México
            </p>
            <p style={{ color: 'var(--cdx-night-faint)', lineHeight: 1.6, position: 'relative', zIndex: 1, maxWidth: 620, margin: '0 auto' }}>
              Cada producto local que eliges fortalece a un productor mexicano, mejora tu salud
              y construye la soberanía alimentaria de nuestra nación.
            </p>
          </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="mt-12 text-center"
      >
        <p style={{ color: 'var(--cdx-ink-soft)', marginBottom: 16 }}>
          Únete al movimiento de mexicanos que eligen salud y productos nacionales.
        </p>
        <a href="/auth/register" className="cdx-btn cdx-btn--primary" style={{ padding: '15px 32px', fontSize: 17 }}>
          Comienza ahora — es gratis
        </a>
      </motion.div>
    </div>
  </section>
);

const InfoCard = ({ title, color, Icon, items, bullet }) => (
  <div style={{ background: 'var(--cdx-paper-2)', border: '1px solid var(--cdx-paper-edge)', borderRadius: 'var(--cdx-r-lg)', padding: '22px', boxShadow: 'var(--cdx-shadow-sm)' }}>
    <div className="flex items-center gap-3 mb-4">
      <div style={{ width: 42, height: 42, borderRadius: 11, background: `color-mix(in srgb, ${color} 16%, transparent)`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon />
      </div>
      <h4 className="cdx-display" style={{ fontSize: 18, color, fontWeight: 600 }}>{title}</h4>
    </div>
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: 'var(--cdx-ink-soft)' }}>
          {bullet === 'check'
            ? <Check color={color} />
            : <span style={{ color, marginTop: 1, fontWeight: 700 }}>•</span>}
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Check = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill={color} aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

/* card header icons (cream stroke on color) */
function StoreIcon() {
  return (<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M4 9l1-4h14l1 4M4 9h16M4 9v1a3 3 0 0 0 6 0 3 3 0 0 0 6 0V9M5 11v8h14v-8" strokeLinejoin="round" strokeLinecap="round" /></svg>);
}
function FlagIcon() {
  return (<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M5 21V4M5 4h11l-2 4 2 4H5" strokeLinejoin="round" strokeLinecap="round" /></svg>);
}
function TagIcon() {
  return (<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M3 7v5l8 8 7-7-8-8H5a2 2 0 0 0-2 2Z" strokeLinejoin="round" /><circle cx="7.5" cy="9.5" r="1.3" fill="currentColor" /></svg>);
}
function SchoolIcon() {
  return (<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M3 9l9-5 9 5-9 5-9-5Z" strokeLinejoin="round" /><path d="M7 11v5c0 1 2.2 2 5 2s5-1 5-2v-5" strokeLinecap="round" /></svg>);
}
function GlobeIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" /></svg>);
}
function AlertIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M12 3l9 16H3l9-16Z" strokeLinejoin="round" /><path d="M12 10v4M12 17h.01" strokeLinecap="round" /></svg>);
}

export default GovernmentSupport;
